import { BarChart3, Droplets, Fuel, Gauge, LineChart, MapPin, Receipt, Store, TrendingUp } from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from "recharts";
import { useAuthUser } from "../auth/AuthProvider";
import { ChartContainer, ChartLegendContent, ChartTooltipContent, type ChartConfig } from "../components/charts/ChartPrimitives";
import { StateBlock } from "../components/common/StateBlock";
import { StatsCounter } from "../components/common/StatsCounter";
import { getStationAnalytics, readCachedStationAnalytics } from "../services/stationAnalyticsService";
import type {
  StationAnalyticsSnapshot,
  StationCStoreCategoryRow,
  StationFuelTrendPoint,
  StationFuelGradeRow,
  StationFuelGradePriceTrendPoint,
  StationMetricValue,
  StationMtdSalesPoint,
  StationPerformanceRow,
  StationSalesTrendPoint,
  StationWeeklySalesPoint
} from "../../shared/types";

const chartColors = {
  axis: "var(--station-axis)"
};

const stationCanopyFileBySk: Record<number, string> = {
  72560: "72560.jpg",
  68908: "68908.png",
  68906: "68906.jpg",
  61327: "61327.png",
  30904: "30904.jpg",
  30903: "30903.jpg",
  30901: "30901.jpg",
  30900: "30900.jpg"
};

const stationCanopyAssetVersion = "20260611-1822";

function getStationCanopyUrl(stationSk?: number): string | undefined {
  if (stationSk === undefined) {
    return undefined;
  }

  const fileName = stationCanopyFileBySk[stationSk];
  return fileName ? `https://gen7fuel.blob.core.windows.net/images/Station/canopy/${fileName}?v=${stationCanopyAssetVersion}` : undefined;
}

function statusCopy(status: string): { title: string; message: string; type: "empty" | "error" | "loading" } {
  if (status === "SETUP_REQUIRED") {
    return {
      type: "error",
      title: "Station access setup required",
      message: "Portal access tables are not deployed yet. Apply the station access schema before approving users."
    };
  }
  if (status === "PENDING") {
    return {
      type: "empty",
      title: "Access request pending",
      message: "Your account is registered. Station_SK access must be approved before station analytics are shown."
    };
  }
  if (status === "SUSPENDED") {
    return {
      type: "error",
      title: "Station access suspended",
      message: "Your station analytics access is currently suspended."
    };
  }
  if (status === "DENIED") {
    return {
      type: "error",
      title: "Station access unavailable",
      message: "Your request for station analytics access was not approved."
    };
  }

  return {
    type: "empty",
    title: "No station access",
    message: "No active Station_SK assignments are available for this account."
  };
}

function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1000000) {
    return `${(value / 1000000).toFixed(abs >= 10000000 ? 1 : 2)}M`;
  }
  if (abs >= 1000) {
    return `${(value / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatCompactFixed(value: number, fractionDigits = 2): string {
  const formatOptions = {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits
  };
  const abs = Math.abs(value);
  if (abs >= 1000000) {
    return `${(value / 1000000).toLocaleString(undefined, formatOptions)}M`;
  }
  if (abs >= 1000) {
    return `${(value / 1000).toLocaleString(undefined, formatOptions)}K`;
  }
  return value.toLocaleString(undefined, formatOptions);
}

function formatMetricValue(metric: StationMetricValue): string {
  if (metric.unit === "currency") {
    return `$${formatCompactFixed(metric.value)}`;
  }
  if (metric.unit === "percent") {
    return `${metric.value.toFixed(2)}%`;
  }
  if (metric.unit === "volume") {
    return `${formatCompactFixed(metric.value)} L`;
  }
  return formatCompactFixed(metric.value);
}

function formatCurrency(value: number): string {
  return `$${formatCompact(value)}`;
}

function formatVolume(value: number): string {
  return `${formatCompact(value)} L`;
}

function chartNumber(value: unknown): number {
  if (Array.isArray(value)) {
    return Number(value[0] ?? 0);
  }
  return Number(value ?? 0);
}

function formatChartCurrency(value: unknown): string {
  return formatCurrency(chartNumber(value));
}

function formatChartVolume(value: unknown): string {
  return formatVolume(chartNumber(value));
}

function formatAxisCategory(value: string): string {
  return value.length > 18 ? `${value.slice(0, 17)}...` : value;
}

function formatDate(value: string): string {
  const businessDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = businessDate
    ? new Date(Date.UTC(Number(businessDate[1]), Number(businessDate[2]) - 1, Number(businessDate[3]), 12))
    : new Date(value);

  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: "America/Toronto", year: "numeric" }).format(date);
}

function formatDateRange(startDate: string, endDate: string): string {
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

function formatDeltaLabel(value: string): string {
  const match = value.trim().match(/^([+-]?\d+(?:\.\d+)?)(pp)$/i);
  if (!match) {
    return value;
  }

  const numericValue = Number(match[1]);
  if (!Number.isFinite(numericValue)) {
    return value;
  }

  return `${numericValue >= 0 ? "+" : ""}${numericValue.toFixed(2)}${match[2].toLowerCase()}`;
}

type MetricTone = "cstore" | "fuel" | "profit" | "neutral";
type DisplayMetricValue = StationMetricValue & { deltaLabel?: string };
interface SparklineSeries {
  current: number[];
  comparison?: number[];
}

function buildCStoreMtdMetrics(snapshot: StationAnalyticsSnapshot): Record<"sales" | "profit" | "gpm" | "transactions", DisplayMetricValue> {
  return snapshot.cstore.mtd;
}

function buildFuelMtdMetrics(snapshot: StationAnalyticsSnapshot): Record<"sales" | "volume" | "price", DisplayMetricValue> {
  return snapshot.fuel.mtd;
}

function buildSparklineSeries(values: number[] | undefined, metric: DisplayMetricValue): SparklineSeries | undefined {
  if (!values || values.length < 2) {
    return undefined;
  }

  // A matched-period YoY cannot reconstruct a prior-year monthly series from the full-YTD trend.
  if (metric.comparison) return { current: values };

  const pointDeltaMatch = metric.unit === "percent" ? metric.deltaLabel?.match(/^([+-]?\d+(?:\.\d+)?)pp$/) : undefined;
  if (pointDeltaMatch) {
    const pointDelta = Number(pointDeltaMatch[1]);
    if (Number.isFinite(pointDelta)) {
      return {
        current: values,
        comparison: values.map((value) => value - pointDelta)
      };
    }
  }

  if (metric.delta === undefined || metric.delta <= -99) {
    return { current: values };
  }

  const comparisonScale = 1 + metric.delta / 100;
  if (!Number.isFinite(comparisonScale) || Math.abs(comparisonScale) < 0.01) {
    return { current: values };
  }

  return {
    current: values,
    comparison: values.map((value) => value / comparisonScale)
  };
}

function Sparkline({ series }: { series?: SparklineSeries }) {
  if (!series?.current || series.current.length < 2) {
    return null;
  }

  const width = 154;
  const height = 44;
  const paddingX = 5;
  const topPadding = 4;
  const plotHeight = 28;
  const axisY = 36;
  const comparison = series.comparison?.length === series.current.length ? series.comparison : undefined;
  const allValues = comparison ? [...series.current, ...comparison] : series.current;
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const range = Math.max(maxValue - minValue, 1);

  function points(values: number[]): string {
    return values
    .map((value, index) => {
      const x = paddingX + (index / (values.length - 1)) * (width - paddingX * 2);
      const y = topPadding + plotHeight - ((value - minValue) / range) * plotHeight;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  }

  return (
    <svg className="station-kpi-sparkline" viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <line className="station-kpi-sparkline__axis" x1={paddingX} x2={width - paddingX} y1={axisY} y2={axisY} />
      {series.current.map((_value, index) => {
        const x = paddingX + (index / (series.current.length - 1)) * (width - paddingX * 2);
        return <line className="station-kpi-sparkline__tick" key={index} x1={x} x2={x} y1={axisY - 2} y2={axisY + 2} />;
      })}
      {comparison && <polyline className="station-kpi-sparkline__comparison" points={points(comparison)} />}
      <polyline className="station-kpi-sparkline__current" points={points(series.current)} />
    </svg>
  );
}

function MetricTile({
  metric,
  icon,
  tone,
  sparkline
}: {
  metric: DisplayMetricValue;
  icon: React.ReactNode;
  tone: MetricTone;
  sparkline?: number[];
}) {
  const trendLabel = metric.deltaLabel ? formatDeltaLabel(metric.deltaLabel) : (
    metric.delta === undefined
      ? undefined
      : `${metric.delta >= 0 ? "+" : ""}${metric.delta.toFixed(2)}%`
  );
  const isNegativeTrend = metric.delta === undefined
    ? metric.deltaLabel?.trim().startsWith("-")
    : metric.delta < 0;
  const sparklineSeries = buildSparklineSeries(sparkline, metric);
  const deltaMatch = metric.deltaLabel?.trim().match(/^([+-]?\d+(?:\.\d+)?)(pp)$/i);
  const deltaValue = deltaMatch ? Number(deltaMatch[1]) : metric.delta;
  const deltaFormatter = deltaMatch
    ? (value: number) => `${value >= 0 ? "+" : ""}${value.toFixed(2)}${deltaMatch[2].toLowerCase()}`
    : (value: number) => `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

  return (
    <article className={`station-kpi station-kpi--${tone}`}>
      <div className="station-kpi__label">
        <div className="station-kpi__title">
          {icon}
          <span>{metric.label}</span>
        </div>
        {trendLabel && <b title={metric.comparison?.detail} className={`station-kpi__delta${isNegativeTrend ? " negative" : ""}`}>
          {deltaValue === undefined ? trendLabel : <StatsCounter duration={0.8} formatValue={deltaFormatter} value={deltaValue} />}
        </b>}
      </div>
      <div className="station-kpi__value-row">
        <strong><StatsCounter formatValue={(value) => formatMetricValue({ ...metric, value })} value={metric.value} /></strong>
        <Sparkline series={sparklineSeries} />
      </div>
      <div className="station-kpi__footer">
        <span title={metric.comparison?.detail}>{metric.comparison?.label || metric.helper}</span>
      </div>
    </article>
  );
}

function ChartPanel({
  eyebrow,
  title,
  detail,
  icon,
  children
}: {
  eyebrow: string;
  title: string;
  detail?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="panel station-chart-panel">
      <div className="panel-heading">
        <div>
          <p>{eyebrow}</p>
          <h3>{title}</h3>
          {detail && <span className="station-chart-panel__detail">{detail}</span>}
        </div>
        {icon}
      </div>
      {children}
    </section>
  );
}

function SalesMixDonut({ cstoreSales, fuelSales }: { cstoreSales: number; fuelSales: number }) {
  const total = cstoreSales + fuelSales;
  const cstorePercent = total > 0 ? cstoreSales / total : 0;
  const chartData = [
    { fill: "var(--color-C-Store)", name: "C-Store", value: cstoreSales },
    { fill: "var(--color-Fuel)", name: "Fuel", value: fuelSales }
  ];
  const chartConfig = {
    "C-Store": {
      color: "var(--station-cstore)",
      formatter: formatChartCurrency,
      label: "C-Store"
    },
    Fuel: {
      color: "var(--station-fuel)",
      formatter: formatChartCurrency,
      label: "Fuel"
    }
  } satisfies ChartConfig;

  return (
    <div className="sales-mix-donut">
      <ChartContainer className="sales-mix-donut__chart" config={chartConfig} height={190} minHeight={190}>
        <PieChart accessibilityLayer>
          <Pie
            data={chartData}
            dataKey="value"
            endAngle={-270}
            innerRadius="68%"
            nameKey="name"
            outerRadius="88%"
            paddingAngle={3}
            startAngle={90}
            stroke="none"
          >
            {chartData.map((entry) => (
              <Cell fill={entry.fill} key={entry.name} />
            ))}
          </Pie>
          <RechartsTooltip content={<ChartTooltipContent />} />
        </PieChart>
      </ChartContainer>
      <div className="sales-mix-donut__center">
        <strong>{Math.round(cstorePercent * 100)}%</strong>
        <span>C-Store</span>
      </div>
      <div className="station-legend">
        <span><i className="legend-swatch legend-swatch--cstore" />C-Store {formatCurrency(cstoreSales)}</span>
        <span><i className="legend-swatch legend-swatch--fuel" />Fuel {formatCurrency(fuelSales)}</span>
      </div>
    </div>
  );
}

function StackedSalesChart({ data }: { data: StationSalesTrendPoint[] }) {
  const chartData = data.map((point) => ({
    ...point,
    totalSales: point.cstoreSales + point.fuelSales
  }));
  const chartConfig = {
    cstoreSales: {
      color: "var(--station-cstore)",
      formatter: formatChartCurrency,
      label: "C-Store sales"
    },
    fuelSales: {
      color: "var(--station-fuel)",
      formatter: formatChartCurrency,
      label: "Fuel sales amount"
    }
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} height={288} minHeight={250}>
      <BarChart accessibilityLayer data={chartData} margin={{ bottom: 0, left: 0, right: 12, top: 16 }}>
        <CartesianGrid stroke={chartColors.axis} vertical={false} />
        <XAxis axisLine={false} dataKey="period" tickLine={false} tickMargin={10} />
        <YAxis axisLine={false} tickFormatter={formatCurrency} tickLine={false} tickMargin={8} width={56} />
        <RechartsTooltip content={<ChartTooltipContent />} cursor={{ fill: "var(--station-cursor-fill)" }} />
        <Legend content={<ChartLegendContent />} itemSorter={null} verticalAlign="bottom" />
        <Bar dataKey="cstoreSales" fill="var(--color-cstoreSales)" radius={[0, 0, 5, 5]} stackId="sales" />
        <Bar dataKey="fuelSales" fill="var(--color-fuelSales)" radius={[5, 5, 0, 0]} stackId="sales" />
      </BarChart>
    </ChartContainer>
  );
}

function WeeklySalesChart({ data }: { data: StationWeeklySalesPoint[] }) {
  const chartConfig = {
    cstoreSales: {
      color: "var(--station-cstore)",
      formatter: formatChartCurrency,
      label: "C-Store sales"
    },
    fuelSales: {
      color: "var(--station-fuel)",
      formatter: formatChartCurrency,
      label: "Fuel sales amount"
    }
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} height={288} minHeight={250}>
      <BarChart accessibilityLayer data={data} margin={{ bottom: 0, left: 0, right: 12, top: 16 }}>
        <CartesianGrid stroke={chartColors.axis} vertical={false} />
        <XAxis axisLine={false} dataKey="day" tickLine={false} tickMargin={10} />
        <YAxis axisLine={false} tickFormatter={formatCurrency} tickLine={false} tickMargin={8} width={56} />
        <RechartsTooltip content={<ChartTooltipContent />} cursor={{ fill: "var(--station-cursor-fill)" }} />
        <Legend content={<ChartLegendContent />} itemSorter={null} verticalAlign="bottom" />
        <Bar barSize={24} dataKey="cstoreSales" fill="var(--color-cstoreSales)" radius={[5, 5, 0, 0]} />
        <Bar barSize={24} dataKey="fuelSales" fill="var(--color-fuelSales)" radius={[5, 5, 0, 0]} />
      </BarChart>
    </ChartContainer>
  );
}

function MtdComparisonLineChart({ data }: { data: StationMtdSalesPoint[] }) {
  const chartData = data.map((point) => ({
    ...point,
    dayLabel: String(point.day)
  }));
  const chartConfig = {
    current: {
      color: "var(--station-mtd-current)",
      formatter: formatChartCurrency,
      label: "This month"
    },
    previous: {
      color: "var(--station-mtd-previous)",
      formatter: formatChartCurrency,
      label: "Last month"
    }
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} height={292} minHeight={250}>
      <RechartsLineChart accessibilityLayer data={chartData} margin={{ bottom: 0, left: 0, right: 18, top: 16 }}>
        <CartesianGrid stroke={chartColors.axis} vertical={false} />
        <XAxis axisLine={false} dataKey="dayLabel" tickLine={false} tickMargin={10} />
        <YAxis axisLine={false} tickFormatter={formatCurrency} tickLine={false} tickMargin={8} width={56} />
        <RechartsTooltip content={<ChartTooltipContent />} cursor={{ stroke: "var(--station-cursor-stroke)" }} />
        <Legend content={<ChartLegendContent />} itemSorter={null} verticalAlign="bottom" />
        <Line
          activeDot={{ r: 5, stroke: "var(--color-panel)", strokeWidth: 2 }}
          dataKey="current"
          dot={{ fill: "var(--color-current)", r: 3, stroke: "var(--color-panel)", strokeWidth: 2 }}
          stroke="var(--color-current)"
          strokeWidth={3}
          type="monotone"
        />
        <Line
          activeDot={{ r: 5, stroke: "var(--color-panel)", strokeWidth: 2 }}
          dataKey="previous"
          dot={{ fill: "var(--color-previous)", r: 3, stroke: "var(--color-panel)", strokeWidth: 2 }}
          stroke="var(--color-previous)"
          strokeWidth={3}
          type="monotone"
        />
      </RechartsLineChart>
    </ChartContainer>
  );
}

function LineSeriesChart({
  data,
  firstKey,
  secondKey,
  firstLabel,
  secondLabel,
  firstFormatter = formatCurrency,
  secondFormatter = formatCurrency
}: {
  data: Array<Record<string, number | string>>;
  firstKey: string;
  secondKey: string;
  firstLabel: string;
  secondLabel: string;
  firstFormatter?: (value: number) => string;
  secondFormatter?: (value: number) => string;
}) {
  const chartConfig = {
    [firstKey]: {
      color: "var(--station-cstore)",
      formatter: (value: unknown) => firstFormatter(chartNumber(value)),
      label: firstLabel
    },
    [secondKey]: {
      color: "var(--station-profit)",
      formatter: (value: unknown) => secondFormatter(chartNumber(value)),
      label: secondLabel
    }
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} height={284} minHeight={250}>
      <RechartsLineChart accessibilityLayer data={data} margin={{ bottom: 0, left: 0, right: 18, top: 16 }}>
        <CartesianGrid stroke={chartColors.axis} vertical={false} />
        <XAxis axisLine={false} dataKey="period" tickLine={false} tickMargin={10} />
        <YAxis axisLine={false} tickFormatter={firstFormatter} tickLine={false} tickMargin={8} width={56} />
        <RechartsTooltip content={<ChartTooltipContent />} cursor={{ stroke: "var(--station-cursor-stroke)" }} />
        <Legend content={<ChartLegendContent />} itemSorter={null} verticalAlign="bottom" />
        <Line
          activeDot={{ r: 5, stroke: "var(--color-panel)", strokeWidth: 2 }}
          dataKey={firstKey}
          dot={{ fill: `var(--color-${firstKey})`, r: 3, stroke: "var(--color-panel)", strokeWidth: 2 }}
          stroke={`var(--color-${firstKey})`}
          strokeWidth={3}
          type="monotone"
        />
        <Line
          activeDot={{ r: 5, stroke: "var(--color-panel)", strokeWidth: 2 }}
          dataKey={secondKey}
          dot={{ fill: `var(--color-${secondKey})`, r: 3, stroke: "var(--color-panel)", strokeWidth: 2 }}
          stroke={`var(--color-${secondKey})`}
          strokeWidth={3}
          type="monotone"
        />
      </RechartsLineChart>
    </ChartContainer>
  );
}

function FuelSalesVolumeChart({ data }: { data: StationFuelTrendPoint[] }) {
  const chartConfig = {
    salesAmount: {
      color: "var(--station-fuel)",
      formatter: formatChartCurrency,
      label: "Fuel sales amount"
    },
    volumeLitres: {
      color: "var(--station-volume-line)",
      formatter: formatChartVolume,
      label: "Fuel volume"
    }
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} height={292} minHeight={250}>
      <ComposedChart accessibilityLayer data={data} margin={{ bottom: 0, left: 0, right: 8, top: 16 }}>
        <CartesianGrid stroke={chartColors.axis} vertical={false} />
        <XAxis axisLine={false} dataKey="period" tickLine={false} tickMargin={10} />
        <YAxis axisLine={false} tickFormatter={formatCurrency} tickLine={false} tickMargin={8} width={56} yAxisId="sales" />
        <YAxis axisLine={false} orientation="right" tickFormatter={formatVolume} tickLine={false} tickMargin={8} width={64} yAxisId="volume" />
        <RechartsTooltip content={<ChartTooltipContent />} cursor={{ fill: "var(--station-cursor-fill)" }} />
        <Legend content={<ChartLegendContent />} itemSorter={null} verticalAlign="bottom" />
        <Bar barSize={32} dataKey="salesAmount" fill="var(--color-salesAmount)" radius={[5, 5, 0, 0]} yAxisId="sales" />
        <Line
          activeDot={{ r: 5, stroke: "var(--color-panel)", strokeWidth: 2 }}
          dataKey="volumeLitres"
          dot={{ fill: "var(--color-volumeLitres)", r: 3, stroke: "var(--color-panel)", strokeWidth: 2 }}
          stroke="var(--color-volumeLitres)"
          strokeWidth={3}
          type="monotone"
          yAxisId="volume"
        />
      </ComposedChart>
    </ChartContainer>
  );
}

function FuelGradeSalesVolumeChart({ data }: { data: StationFuelGradeRow[] }) {
  const chartData = data.map((row) => ({
    ...row,
    shortGrade: formatAxisCategory(row.grade)
  }));
  const chartConfig = {
    salesAmount: {
      color: "var(--station-fuel)",
      formatter: formatChartCurrency,
      label: "Sales amount YTD"
    },
    volumeLitres: {
      color: "var(--station-volume-line)",
      formatter: formatChartVolume,
      label: "Volume YTD"
    }
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} height={312} minHeight={270}>
      <ComposedChart accessibilityLayer data={chartData} margin={{ bottom: 0, left: 0, right: 8, top: 16 }}>
        <CartesianGrid stroke={chartColors.axis} vertical={false} />
        <XAxis axisLine={false} dataKey="shortGrade" tickLine={false} tickMargin={10} />
        <YAxis axisLine={false} tickFormatter={formatCurrency} tickLine={false} tickMargin={8} width={56} yAxisId="sales" />
        <YAxis axisLine={false} orientation="right" tickFormatter={formatVolume} tickLine={false} tickMargin={8} width={64} yAxisId="volume" />
        <RechartsTooltip content={<ChartTooltipContent />} cursor={{ fill: "var(--station-cursor-fill)" }} />
        <Legend content={<ChartLegendContent />} itemSorter={null} verticalAlign="bottom" />
        <Bar barSize={28} dataKey="salesAmount" fill="var(--color-salesAmount)" radius={[5, 5, 0, 0]} yAxisId="sales" />
        <Line
          activeDot={{ r: 5, stroke: "var(--color-panel)", strokeWidth: 2 }}
          dataKey="volumeLitres"
          dot={{ fill: "var(--color-volumeLitres)", r: 3, stroke: "var(--color-panel)", strokeWidth: 2 }}
          stroke="var(--color-volumeLitres)"
          strokeWidth={3}
          type="monotone"
          yAxisId="volume"
        />
      </ComposedChart>
    </ChartContainer>
  );
}

const gradePricePalette = [
  "#a78bfa",
  "#c084fc",
  "#8b5cf6",
  "#d8b4fe",
  "#7c3aed",
  "#f0abfc",
  "#818cf8",
  "#e879f9"
];

function FuelGradePriceTrendChart({ data }: { data: StationFuelGradePriceTrendPoint[] }) {
  const grades = [...new Set(data.map((point) => point.grade))];
  const seriesByGrade = new Map(grades.map((grade, index) => [grade, `grade_${index}`]));
  const rowsByDate = new Map<string, Record<string, number | string>>();

  data.forEach((point) => {
    const key = seriesByGrade.get(point.grade);
    if (!key) {
      return;
    }
    const row = rowsByDate.get(point.date) || { date: point.date, period: point.period };
    row[key] = point.avgRetailPrice;
    rowsByDate.set(point.date, row);
  });

  const chartData = [...rowsByDate.values()].sort((left, right) => String(left.date).localeCompare(String(right.date)));
  const chartConfig = Object.fromEntries(
    grades.map((grade, index) => [
      `grade_${index}`,
      {
        color: gradePricePalette[index % gradePricePalette.length],
        formatter: (value: unknown) => `$${chartNumber(value).toFixed(3)}/L`,
        label: grade
      }
    ])
  ) satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} height={336} minHeight={290}>
      <RechartsLineChart accessibilityLayer data={chartData} margin={{ bottom: 0, left: 0, right: 18, top: 16 }}>
        <CartesianGrid stroke={chartColors.axis} vertical={false} />
        <XAxis axisLine={false} dataKey="period" minTickGap={28} tickLine={false} tickMargin={10} />
        <YAxis axisLine={false} tickFormatter={(value) => `$${Number(value).toFixed(2)}`} tickLine={false} tickMargin={8} width={56} />
        <RechartsTooltip content={<ChartTooltipContent />} cursor={{ stroke: "var(--station-cursor-stroke)" }} />
        <Legend content={<ChartLegendContent />} itemSorter={null} verticalAlign="bottom" />
        {grades.map((grade, index) => {
          const key = `grade_${index}`;
          return (
            <Line
              activeDot={{ r: 5, stroke: "var(--color-panel)", strokeWidth: 2 }}
              connectNulls
              dataKey={key}
              dot={false}
              key={grade}
              stroke={`var(--color-${key})`}
              strokeWidth={2.4}
              type="monotone"
            />
          );
        })}
      </RechartsLineChart>
    </ChartContainer>
  );
}

function HorizontalBarValueLabel({
  height,
  payload,
  value,
  viewBox,
  width,
  x,
  y
}: {
  height?: number | string;
  payload?: { formatted?: string };
  value?: unknown;
  viewBox?: { height?: number | string; width?: number | string; x?: number | string; y?: number | string };
  width?: number | string;
  x?: number | string;
  y?: number | string;
}) {
  const labelValue = payload?.formatted || value;

  if (!labelValue) {
    return <g />;
  }

  const nextX = chartNumber(x ?? viewBox?.x) + chartNumber(width ?? viewBox?.width) + 8;
  const nextY = chartNumber(y ?? viewBox?.y) + chartNumber(height ?? viewBox?.height) / 2 + 4;

  return (
    <text className="station-bar-value-label" x={nextX} y={nextY}>
      {String(labelValue)}
    </text>
  );
}

function HorizontalBarList<T>({
  rows,
  value,
  label,
  formatter,
  accent = "cstore",
  secondary
}: {
  rows: T[];
  value: (row: T) => number;
  label: (row: T) => string;
  formatter: (value: number) => string;
  accent?: "cstore" | "fuel" | "profit";
  secondary?: (row: T) => string;
}) {
  const chartData = rows.map((row) => {
    const rowValue = value(row);
    return {
      amount: rowValue,
      formatted: formatter(rowValue),
      name: label(row),
      secondary: secondary?.(row)
    };
  });
  const chartConfig = {
    amount: {
      color: `var(--station-${accent})`,
      formatter: (nextValue: unknown) => formatter(chartNumber(nextValue)),
      label: "Value"
    }
  } satisfies ChartConfig;

  return (
    <ChartContainer className="station-horizontal-chart" config={chartConfig} height={Math.max(240, rows.length * 43)} minHeight={220}>
      <BarChart accessibilityLayer data={chartData} layout="vertical" margin={{ bottom: 0, left: 0, right: 72, top: 0 }}>
        <CartesianGrid horizontal={false} stroke={chartColors.axis} />
        <XAxis hide type="number" />
        <YAxis
          axisLine={false}
          dataKey="name"
          tickFormatter={formatAxisCategory}
          tickLine={false}
          type="category"
          width={118}
        />
        <RechartsTooltip content={<ChartTooltipContent hideLabel secondaryKey="secondary" />} cursor={{ fill: "var(--station-cursor-fill)" }} />
        <Bar
          barSize={12}
          dataKey="amount"
          fill="var(--color-amount)"
          label={{ content: <HorizontalBarValueLabel />, dataKey: "formatted" }}
          radius={[0, 999, 999, 0]}
        />
      </BarChart>
    </ChartContainer>
  );
}

function StationPerformanceTable({ rows }: { rows: StationPerformanceRow[] }) {
  return (
    <div className="table-scroll station-table-scroll">
      <table className="data-table compact station-performance-table">
        <thead>
          <tr>
            <th>Station</th>
            <th>C-Store Sales</th>
            <th>Fuel Sales Amount</th>
            <th>Fuel Volume</th>
            <th>Gross Profit</th>
            <th>Transactions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.stationSk}>
              <td>
                <strong>{row.stationName}</strong>
                <span>{row.province} / {row.stationSk}</span>
              </td>
              <td><StatsCounter formatValue={formatCurrency} value={row.cstoreSales} /></td>
              <td><StatsCounter formatValue={formatCurrency} value={row.fuelSales} /></td>
              <td><StatsCounter formatValue={formatVolume} value={row.fuelVolumeLitres} /></td>
              <td><StatsCounter formatValue={formatCurrency} value={row.grossProfit} /></td>
              <td><StatsCounter formatValue={formatCompact} value={row.transactions} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FuelGradeTable({ rows }: { rows: StationFuelGradeRow[] }) {
  return (
    <div className="station-grade-list">
      {rows.map((row) => (
        <article className="station-grade-card" key={row.grade}>
          <div className="station-grade-card__heading">
            <span>{row.grade}</span>
            <em>YTD</em>
          </div>
          <strong><StatsCounter formatValue={formatCurrency} value={row.salesAmount} /></strong>
          <div className="station-grade-card__details">
            <small><StatsCounter formatValue={formatVolume} value={row.volumeLitres} /></small>
            <small><StatsCounter formatValue={(value) => `$${value.toFixed(3)}/L`} value={row.avgRetailPrice} /></small>
          </div>
        </article>
      ))}
    </div>
  );
}

interface StationAnalyticsPageProps {
  selectedStationSk?: number;
}

export function StationAnalyticsPage({ selectedStationSk }: StationAnalyticsPageProps) {
  const { portalContext } = useAuthUser();
  const [snapshot, setSnapshot] = useState<StationAnalyticsSnapshot>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const stationSks = useMemo(
    () => portalContext?.stations.map((station) => station.stationSk).filter((stationSk) => Number.isFinite(stationSk)) || [],
    [portalContext]
  );
  const requestedStationSks = useMemo(
    () => (selectedStationSk === undefined ? stationSks : [selectedStationSk]),
    [selectedStationSk, stationSks]
  );

  useEffect(() => {
    if (!portalContext?.features.stationAnalytics) {
      return;
    }

    let isMounted = true;
    const cachedSnapshot = readCachedStationAnalytics(requestedStationSks);
    if (cachedSnapshot) {
      setSnapshot(cachedSnapshot);
      setIsLoading(false);
    } else {
      setSnapshot(undefined);
      setIsLoading(true);
    }
    setError(undefined);

    const load = () => getStationAnalytics(requestedStationSks, "ytd", { forceRefresh: true })
      .then((nextSnapshot) => {
        if (isMounted) {
          setError(undefined);
          setSnapshot(nextSnapshot);
        }
      })
      .catch((err: Error) => {
        if (isMounted) {
          setSnapshot(undefined);
          setError(err.message);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });
    void load();
    const timer = window.setInterval(() => { void load(); }, 60_000);

    return () => {
      isMounted = false;
      window.clearInterval(timer);
    };
  }, [portalContext?.features.stationAnalytics, requestedStationSks]);

  const stationOptions = useMemo(() => {
    const portalStations = portalContext?.stations || [];
    if (!snapshot) {
      return portalStations;
    }
    const allowed = new Set(stationSks);
    const matched = snapshot.stationPerformance.filter((station) => allowed.has(station.stationSk));
    return matched.length > 0 ? matched : portalStations;
  }, [portalContext?.stations, snapshot, stationSks]);

  const scopedSnapshot = snapshot;

  if (!portalContext) {
    return <StateBlock type="loading" title="Loading station access" message="Preparing your station analytics workspace." />;
  }

  if (!portalContext.features.stationAnalytics) {
    const copy = statusCopy(portalContext.accessStatus);
    return <StateBlock type={copy.type} title={copy.title} message={copy.message} />;
  }

  if (error) {
    return <StateBlock type="error" title="Station analytics unavailable" message={error} />;
  }

  if (isLoading || !scopedSnapshot) {
    return <StateBlock type="loading" title="Loading station analytics" message="Loading saved C-Store and Fuel data." />;
  }

  const overviewMetrics = scopedSnapshot.overview;
  const cstoreMetrics = scopedSnapshot.cstore.metrics;
  const fuelMetrics = scopedSnapshot.fuel.metrics;
  const cstoreMtdMetrics = buildCStoreMtdMetrics(scopedSnapshot);
  const fuelMtdMetrics = buildFuelMtdMetrics(scopedSnapshot);
  const isAllStationsScope = selectedStationSk === undefined;
  const totalCStore = overviewMetrics.cstoreSalesYtd.value;
  const totalFuel = overviewMetrics.fuelSalesYtd.value;
  const selectedStationLabel = selectedStationSk === undefined
    ? "All approved stations"
    : scopedSnapshot.stationPerformance.find((station) => station.stationSk === selectedStationSk)?.stationName ||
      stationOptions.find((station) => station.stationSk === selectedStationSk)?.stationName ||
      portalContext.stations.find((station) => station.stationSk === selectedStationSk)?.stationName ||
      portalContext.stations.find((station) => station.stationSk === selectedStationSk)?.businessUnitName ||
      `Station ${selectedStationSk}`;
  const selectedStationCanopyUrl = getStationCanopyUrl(selectedStationSk);
  const stationHeadingStyle = selectedStationCanopyUrl
    ? ({ "--station-canopy-image": `url("${selectedStationCanopyUrl}")` } as CSSProperties)
    : undefined;
  const overviewSparkline = {
    totalSalesYtd: scopedSnapshot.monthlyTrend.map((point) => point.cstoreSales + point.fuelSales),
    cstoreSalesYtd: scopedSnapshot.monthlyTrend.map((point) => point.cstoreSales),
    fuelSalesYtd: scopedSnapshot.monthlyTrend.map((point) => point.fuelSales),
    grossProfitYtd: scopedSnapshot.monthlyTrend.map((point) => point.grossProfit),
    fuelVolumeYtd: scopedSnapshot.monthlyTrend.map((point) => point.fuelVolumeLitres),
    transactionsYtd: scopedSnapshot.cstore.trend.map((point) => point.transactions)
  };
  const cstoreSparkline = {
    salesYtd: scopedSnapshot.cstore.trend.map((point) => point.sales),
    profitYtd: scopedSnapshot.cstore.trend.map((point) => point.profit),
    gpmYtd: scopedSnapshot.cstore.trend.map((point) => (point.sales > 0 ? (point.profit / point.sales) * 100 : 0)),
    transactionsYtd: scopedSnapshot.cstore.trend.map((point) => point.transactions)
  };
  const fuelSparkline = {
    salesYtd: scopedSnapshot.fuel.trend.map((point) => point.salesAmount),
    volumeYtd: scopedSnapshot.fuel.trend.map((point) => point.volumeLitres),
    avgRetailPriceYtd: scopedSnapshot.fuel.trend.map((point) => point.avgRetailPrice),
    poolMarginYtd: scopedSnapshot.fuel.grades.map((point) => point.poolMargin)
  };
  const weeklySalesRange = formatDateRange(scopedSnapshot.weeklySalesRange.startDate, scopedSnapshot.weeklySalesRange.endDate);

  return (
    <section className="page-stack station-analytics-page">
      <div
        className={selectedStationCanopyUrl ? "page-heading station-page-heading station-page-heading--canopy" : "page-heading station-page-heading"}
        style={stationHeadingStyle}
      >
        <div>
          <p>Station analytics</p>
          <h2>Stations</h2>
        </div>
        <div className="station-toolbar">
          <span className="timestamp-pill">{scopedSnapshot.periodLabel}</span>
          <span className="timestamp-pill">As of {formatDate(scopedSnapshot.asOfDate)}</span>
          <div className="station-scope-pill" aria-label="Selected station scope">
            <MapPin size={15} aria-hidden="true" />
            <span>{selectedStationLabel}</span>
          </div>
        </div>
      </div>

      {scopedSnapshot.delivery && (
        <p role="status" className="muted-text">
          {scopedSnapshot.delivery.status === "stale" ? "Update delayed. Showing last saved data." : "Saved data."}
          {" "}Model refreshed: {new Date(scopedSnapshot.delivery.sourceRefreshedAt).toLocaleString("en-CA", { timeZone: "America/Toronto" })} ET.
          {" "}Snapshot saved: {new Date(scopedSnapshot.delivery.publishedAt).toLocaleString("en-CA", { timeZone: "America/Toronto" })} ET.
        </p>
      )}

      <section className="station-overview-grid">
        <div className="station-kpi-grid">
          <MetricTile metric={overviewMetrics.totalSalesYtd} icon={<TrendingUp size={16} aria-hidden="true" />} tone="neutral" sparkline={overviewSparkline.totalSalesYtd} />
          <MetricTile metric={overviewMetrics.cstoreSalesYtd} icon={<Store size={16} aria-hidden="true" />} tone="cstore" sparkline={overviewSparkline.cstoreSalesYtd} />
          <MetricTile metric={overviewMetrics.fuelSalesYtd} icon={<Fuel size={16} aria-hidden="true" />} tone="fuel" sparkline={overviewSparkline.fuelSalesYtd} />
          <MetricTile metric={overviewMetrics.grossProfitYtd} icon={<Gauge size={16} aria-hidden="true" />} tone="profit" sparkline={overviewSparkline.grossProfitYtd} />
          <MetricTile metric={overviewMetrics.fuelVolumeYtd} icon={<Droplets size={16} aria-hidden="true" />} tone="fuel" sparkline={overviewSparkline.fuelVolumeYtd} />
          <MetricTile metric={overviewMetrics.transactionsYtd} icon={<Receipt size={16} aria-hidden="true" />} tone="neutral" sparkline={overviewSparkline.transactionsYtd} />
        </div>

        <ChartPanel eyebrow="Overview" title="YTD Sales Mix" icon={<BarChart3 size={18} aria-hidden="true" />}>
          <SalesMixDonut cstoreSales={totalCStore} fuelSales={totalFuel} />
        </ChartPanel>
      </section>

      <div className="station-chart-grid station-chart-grid--overview">
        <ChartPanel eyebrow="Overview" title="YTD Monthly Sales Amount" icon={<BarChart3 size={18} aria-hidden="true" />}>
          <StackedSalesChart data={scopedSnapshot.monthlyTrend} />
        </ChartPanel>

        <ChartPanel eyebrow="Overview" title="WTD Sales Amount by Day" detail={weeklySalesRange} icon={<BarChart3 size={18} aria-hidden="true" />}>
          <WeeklySalesChart data={scopedSnapshot.weeklySales} />
        </ChartPanel>

        {isAllStationsScope && (
          <>
            <ChartPanel eyebrow="Overview" title="Station Contribution" icon={<MapPin size={18} aria-hidden="true" />}>
              <HorizontalBarList
                rows={[...scopedSnapshot.stationPerformance].sort((a, b) => (b.cstoreSales + b.fuelSales) - (a.cstoreSales + a.fuelSales))}
                label={(row) => row.stationName}
                value={(row) => row.cstoreSales + row.fuelSales}
                formatter={formatCurrency}
                accent="fuel"
                secondary={(row) => `${formatCurrency(row.cstoreSales)} C-Store / ${formatCurrency(row.fuelSales)} Fuel`}
              />
            </ChartPanel>

            <ChartPanel eyebrow="Overview" title="Fuel Contribution" icon={<Fuel size={18} aria-hidden="true" />}>
              <HorizontalBarList
                rows={[...scopedSnapshot.stationPerformance].sort((a, b) => b.fuelSales - a.fuelSales)}
                label={(row) => row.stationName}
                value={(row) => row.fuelSales}
                formatter={formatCurrency}
                accent="fuel"
                secondary={(row) => `${formatVolume(row.fuelVolumeLitres)} volume`}
              />
            </ChartPanel>
          </>
        )}
      </div>

      <section className="station-section-heading">
        <div>
          <p>MTD / MoM</p>
          <h3>Daily sales comparison</h3>
        </div>
      </section>

      <div className="station-chart-grid">
        <ChartPanel eyebrow="C-Store" title="MTD Sales Amount by Day" icon={<LineChart size={18} aria-hidden="true" />}>
          <MtdComparisonLineChart data={scopedSnapshot.cstore.mtdTrend} />
        </ChartPanel>

        <ChartPanel eyebrow="Fuel" title="MTD Sales Amount by Day" icon={<LineChart size={18} aria-hidden="true" />}>
          <MtdComparisonLineChart data={scopedSnapshot.fuel.mtdTrend} />
        </ChartPanel>
      </div>

      <section className="station-section-heading">
        <div>
          <p>C-Store</p>
          <h3>Sales, margin, and transactions</h3>
        </div>
      </section>

      <div className="station-kpi-grid station-kpi-grid--section">
        <MetricTile metric={cstoreMetrics.salesYtd} icon={<Store size={16} aria-hidden="true" />} tone="cstore" sparkline={cstoreSparkline.salesYtd} />
        <MetricTile metric={cstoreMetrics.profitYtd} icon={<Gauge size={16} aria-hidden="true" />} tone="profit" sparkline={cstoreSparkline.profitYtd} />
        <MetricTile metric={cstoreMetrics.gpmYtd} icon={<TrendingUp size={16} aria-hidden="true" />} tone="profit" sparkline={cstoreSparkline.gpmYtd} />
        <MetricTile metric={cstoreMetrics.transactionsYtd} icon={<Receipt size={16} aria-hidden="true" />} tone="neutral" sparkline={cstoreSparkline.transactionsYtd} />
      </div>

      <div className="station-period-heading">
        <p>MTD / MoM</p>
      </div>

      <div className="station-kpi-grid station-kpi-grid--period">
        <MetricTile metric={cstoreMtdMetrics.sales} icon={<Store size={16} aria-hidden="true" />} tone="cstore" sparkline={cstoreSparkline.salesYtd} />
        <MetricTile metric={cstoreMtdMetrics.profit} icon={<Gauge size={16} aria-hidden="true" />} tone="profit" sparkline={cstoreSparkline.profitYtd} />
        <MetricTile metric={cstoreMtdMetrics.gpm} icon={<TrendingUp size={16} aria-hidden="true" />} tone="profit" sparkline={cstoreSparkline.gpmYtd} />
        <MetricTile metric={cstoreMtdMetrics.transactions} icon={<Receipt size={16} aria-hidden="true" />} tone="neutral" sparkline={cstoreSparkline.transactionsYtd} />
      </div>

      <div className="station-chart-grid">
        <ChartPanel eyebrow="C-Store" title="Sales and Gross Profit Trend" icon={<LineChart size={18} aria-hidden="true" />}>
          <LineSeriesChart
            data={scopedSnapshot.cstore.trend as unknown as Array<Record<string, number | string>>}
            firstKey="sales"
            secondKey="profit"
            firstLabel="Sales"
            secondLabel="Gross profit"
          />
        </ChartPanel>

        <ChartPanel eyebrow="C-Store" title="Department Sales Mix" icon={<BarChart3 size={18} aria-hidden="true" />}>
          <HorizontalBarList<StationCStoreCategoryRow>
            rows={scopedSnapshot.cstore.categories}
            label={(row) => row.category}
            value={(row) => row.sales}
            formatter={formatCurrency}
            accent="cstore"
            secondary={(row) => `${formatCurrency(row.profit)} profit / ${row.gpm.toFixed(1)}% GPM`}
          />
        </ChartPanel>
      </div>

      <section className="station-section-heading">
        <div>
          <p>Fuel</p>
          <h3>Sales amount, volume, and grade mix</h3>
        </div>
      </section>

      <div className="station-kpi-grid station-kpi-grid--section">
        <MetricTile metric={fuelMetrics.salesYtd} icon={<Fuel size={16} aria-hidden="true" />} tone="fuel" sparkline={fuelSparkline.salesYtd} />
        <MetricTile metric={fuelMetrics.volumeYtd} icon={<Droplets size={16} aria-hidden="true" />} tone="fuel" sparkline={fuelSparkline.volumeYtd} />
        <MetricTile metric={fuelMetrics.avgRetailPriceYtd} icon={<Gauge size={16} aria-hidden="true" />} tone="neutral" sparkline={fuelSparkline.avgRetailPriceYtd} />
        <MetricTile metric={fuelMetrics.poolMarginYtd} icon={<TrendingUp size={16} aria-hidden="true" />} tone="profit" sparkline={fuelSparkline.poolMarginYtd} />
      </div>

      <div className="station-period-heading">
        <p>MTD / MoM</p>
      </div>

      <div className="station-kpi-grid station-kpi-grid--period">
        <MetricTile metric={fuelMtdMetrics.sales} icon={<Fuel size={16} aria-hidden="true" />} tone="fuel" sparkline={fuelSparkline.salesYtd} />
        <MetricTile metric={fuelMtdMetrics.volume} icon={<Droplets size={16} aria-hidden="true" />} tone="fuel" sparkline={fuelSparkline.volumeYtd} />
        <MetricTile metric={fuelMtdMetrics.price} icon={<Gauge size={16} aria-hidden="true" />} tone="profit" sparkline={fuelSparkline.avgRetailPriceYtd} />
      </div>

      <div className="station-chart-grid">
        <ChartPanel eyebrow="Fuel" title="Fuel Sales Amount and Volume" icon={<LineChart size={18} aria-hidden="true" />}>
          <FuelSalesVolumeChart data={scopedSnapshot.fuel.trend} />
        </ChartPanel>

        <ChartPanel eyebrow="Fuel" title="Fuel Grade Mix" icon={<Fuel size={18} aria-hidden="true" />}>
          <HorizontalBarList<StationFuelGradeRow>
            rows={scopedSnapshot.fuel.grades}
            label={(row) => row.grade}
            value={(row) => row.salesAmount}
            formatter={formatCurrency}
            accent="fuel"
            secondary={(row) => `${formatVolume(row.volumeLitres)} / $${row.avgRetailPrice.toFixed(3)}/L`}
          />
        </ChartPanel>
      </div>

      <ChartPanel eyebrow="Fuel" title="Grade Sales Amount and Volume YTD" icon={<Fuel size={18} aria-hidden="true" />}>
        <FuelGradeSalesVolumeChart data={scopedSnapshot.fuel.grades} />
      </ChartPanel>

      <ChartPanel eyebrow="Fuel" title="Grade Avg Retail Price Trend YTD" detail={`Jan 1 - ${formatDate(scopedSnapshot.asOfDate)}`} icon={<LineChart size={18} aria-hidden="true" />}>
        <FuelGradePriceTrendChart data={scopedSnapshot.fuel.gradePriceTrend} />
      </ChartPanel>

      <ChartPanel eyebrow="Stations" title="YTD Station Performance" icon={<MapPin size={18} aria-hidden="true" />}>
        <StationPerformanceTable rows={scopedSnapshot.stationPerformance} />
      </ChartPanel>

      <ChartPanel eyebrow="Fuel" title="Grade Cards YTD" icon={<Fuel size={18} aria-hidden="true" />}>
        <FuelGradeTable rows={scopedSnapshot.fuel.grades} />
      </ChartPanel>
    </section>
  );
}
